/**
 * A panel that slides in from the right, for editing without leaving the list.
 *
 * A drawer, not a modal, on purpose. The point of editing a contact from the
 * contact list is that the list stays visible: you can see the row you are
 * editing in the context of its neighbours. A modal hides the very thing that
 * makes the edit safe.
 *
 * On a narrow screen it takes the full width, because a 480px panel on a 360px
 * phone is a panel with a sliver of the page behind it.
 */
import { useEffect, type ReactNode } from 'react';
import { X } from 'lucide-react';
import { cn } from '../lib/cn';

export interface DrawerProps {
  readonly open: boolean;
  readonly title: string;
  readonly onClose: () => void;
  readonly children: ReactNode;
  readonly footer?: ReactNode;
  /** Escape and backdrop dismissal are refused while true. */
  readonly locked?: boolean;
  readonly className?: string;
}

export function Drawer({ open, title, onClose, children, footer, locked = false, className }: DrawerProps) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !locked) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, locked, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-40 flex justify-end bg-black/60"
      onClick={() => {
        if (!locked) onClose();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        data-testid="drawer"
        // Full width on a phone, a panel on a desktop. `max-w-full` is what stops
        // the panel forcing a horizontal scrollbar on a 360px screen.
        className={cn(
          'flex h-full w-full max-w-full flex-col border-l border-slate-800 bg-slate-900 sm:max-w-md',
          className,
        )}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-4 border-b border-slate-800 px-4 py-3">
          <h2 className="text-sm font-semibold text-slate-100">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            disabled={locked}
            aria-label="Close"
            className="text-slate-500 hover:text-slate-200 disabled:opacity-40"
          >
            <X className="h-4 w-4" aria-hidden />
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto p-4 text-sm text-slate-300">{children}</div>
        {footer ? <div className="flex justify-end gap-2 border-t border-slate-800 px-4 py-3">{footer}</div> : null}
      </div>
    </div>
  );
}
